// LimitReachedModal.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { formatBytes } from 'main/sidebar/switcher/popup/settings/utils/settingsPlanPaneUtils';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';

const LIMIT_COPY = Object.freeze({
  api: {
    adminAction: 'Upgrade Plan',
    adminBody: 'This space has reached its monthly API run limit. Upgrade the plan or wait until the next billing month.',
    memberBody: 'This space has reached its monthly API run limit. Contact a base owner or admin for help.',
    title: 'API limit reached',
  },
  file_size: {
    adminAction: 'View Plan',
    adminBody: 'This file exceeds the maximum upload size allowed by your base plan.',
    memberBody: 'This file exceeds the maximum upload size allowed by your base plan. Contact a base owner or admin if you need a larger limit.',
    title: 'File too large',
  },
  members: {
    adminAction: 'Upgrade Plan',
    adminBody: 'This space has reached its member limit for the current plan.',
    memberBody: 'This space has reached its member limit. Contact a base owner or admin to upgrade the plan.',
    title: 'Member limit reached',
  },
  storage: {
    adminAction: 'Upgrade Plan',
    adminBody: 'This space is out of storage. Upgrade the plan or delete files to free up space.',
    memberBody: 'This space is out of storage. Contact a base owner or admin to upgrade the plan or free up space.',
    title: 'Storage limit reached',
  },
  live: {
    adminAction: 'Upgrade Plan',
    adminBody: 'This space has reached its live usage limit for the current plan.',
    memberBody: 'This space has reached its live usage limit. Contact a base owner or admin for help.',
    title: 'Live limit reached',
  },
  usage_allowance: {
    adminAction: 'Upgrade Plan',
    adminBody: 'This space has used its included usage for this month. Upgrade the plan for more AI and API usage, or wait until the allowance resets.',
    memberBody: 'This space has used its included usage for this month. Contact a base owner or admin to upgrade the plan.',
    title: 'Usage limit reached',
  },
  teamUsage: {
    adminAction: 'Upgrade Plan',
    adminBody: 'This space has used its included usage for this month. Upgrade the plan for more AI and API usage, or wait until the allowance resets.',
    memberBody: 'This space has used its included usage for this month. Contact a base owner or admin to upgrade the plan.',
    title: 'Usage limit reached',
  },
  ai_disabled: {
    adminAction: 'Upgrade Plan',
    adminBody: 'Ai is not included on this base plan. Upgrade to a plan that includes AI.',
    memberBody: 'Ai is not included on this base plan. Contact a base owner or admin to upgrade.',
    title: 'AI not available',
  },
});

/**
 * formatLimitValue
 *
 * Formats a base limit value for display in the modal body.
 *
 * @param {string} limitType - The limit category being formatted.
 * @param {number} limit - The raw limit value.
 * @returns {string|null} The formatted limit label.
 */
function formatLimitValue(limitType, limit) {
  const parsed = Number(limit);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  if (limitType === 'storage' || limitType === 'file_size') {
    if (parsed < 1) return null;
    return formatBytes(parsed);
  }

  if (limitType === 'usage_allowance' || limitType === 'teamUsage') {
    return `${parsed.toLocaleString()} credits`;
  }

  if (parsed < 1) {
    return null;
  }

  return parsed.toLocaleString();
}

/**
 * LimitReachedModal
 *
 * Displays a base limit warning with optional upgrade action for admins.
 *
 * @param {boolean} actorCanUpgrade - Whether the current user can open plan settings.
 * @param {boolean} isOpen - Whether the modal is visible.
 * @param {number} limit - The configured limit value.
 * @param {string} limitType - The limit category key.
 * @param {string} message - Optional override for the modal body copy.
 * @param {function} onAdminAction - Called when the upgrade action is clicked.
 * @param {function} onClose - Called when the modal should close.
 * @param {number} used - The current usage value.
 *
 * @returns {ReactNode} The rendered limit modal.
 */
const LimitReachedModal = React.memo(({
  actorCanUpgrade = false,
  isOpen,
  limit,
  limitType = 'storage',
  message,
  onAdminAction,
  onClose,
  used,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // Resolves copy and usage label for the active limit type.
  const copy = LIMIT_COPY[limitType] || LIMIT_COPY.storage;

  // ----- MEMOS ----- //

  const usageLabel = useMemo(() => {
    const limitLabel = formatLimitValue(limitType, limit);
    const usedLabel = formatLimitValue(limitType, used);

    if (usedLabel && limitLabel) {
      return `${usedLabel} / ${limitLabel}`;
    }

    if (limitLabel) {
      return limitLabel;
    }

    return null;
  }, [limit, limitType, used]);

  const body = message
    || (actorCanUpgrade ? copy.adminBody : copy.memberBody);

  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="warning"
      isOpen={isOpen}
      title={copy.title}
      width={isMobile ? '100%' : 420}
      onClose={onClose}
    >
      <ModalForm>
        <p
          style={{
            color: theme.foregroundMuted,
            margin: '0 0 12px',
          }}
        >
          {body}
        </p>

        {usageLabel ? (
          <p
            style={{
              color: theme.foreground,
              fontWeight: 600,
              margin: '0 0 16px',
            }}
          >
            {usageLabel}
          </p>
        ) : null}
      </ModalForm>

      <ModalButtons>
        <Button
          borderRadius="6px"
          fontSize="12px"
          height="36px"
          label="Close"
          width="auto"
          onClick={onClose}
        />

        {actorCanUpgrade && onAdminAction ? (
          <Button
            borderRadius="6px"
            fontSize="12px"
            height="36px"
            iconKey="paid"
            iconSize={16}
            label={copy.adminAction}
            width="auto"
            onClick={onAdminAction}
          />
        ) : null}
      </ModalButtons>
    </Modal>
  );
});

// Dev Tools
LimitReachedModal.displayName = 'LimitReachedModal';

export default LimitReachedModal;
